import React, { useState } from 'react';
import { Settings, BookOpen, FlaskConical } from 'lucide-react';

interface LandingViewProps {
  onConfigure: () => void;
  onExplore: () => void;
  onTest: () => void;
}

const Card: React.FC<{
  onClick: () => void;
  icon: React.ReactNode;
  iconBg: string;
  iconBgHover: string;
  title: string;
  description: string;
  cta: string;
  ctaColor: string;
}> = ({ onClick, icon, iconBg, iconBgHover, title, description, cta, ctaColor }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor: 'pointer', backgroundColor: '#fff', borderRadius: 14, padding: '28px',
        border: '1px solid', borderColor: hovered ? ctaColor + '60' : '#e2e8f0',
        boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.04)',
        transition: 'all 0.2s ease',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div style={{ width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: hovered ? iconBgHover : iconBg, marginBottom: 20, transition: 'background-color 0.2s' }}>
        {icon}
      </div>
      <div style={{ fontSize: 20, color: '#0f172a', margin: '0 0 10px 0', lineHeight: 1.3 }}><b>{title}</b></div>
      <div style={{ fontSize: 14, color: '#475569', marginBottom: 24, lineHeight: 1.65, flex: 1 }}>{description}</div>
      <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 14, fontWeight: 600, color: ctaColor }}>
        {cta}
        <svg style={{ width: 16, height: 16, marginLeft: 6 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </span>
    </div>
  );
};

export const LandingView: React.FC<LandingViewProps> = ({ onConfigure, onExplore, onTest }) => {
  const cols = 'repeat(3, 1fr)';

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 24, color: '#0f172a', margin: '0 0 8px 0' }}><b>Analytics</b></div>
        <div style={{ fontSize: 15, color: '#475569', margin: 0, lineHeight: 1.5 }}>
          Configure analytics journeys, explore available attributes, and test your setup with real data.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 20, maxWidth: 1100 }}>
        <Card
          onClick={onConfigure}
          icon={<Settings style={{ width: 24, height: 24, color: '#2563eb' }} />}
          iconBg="#eff6ff" iconBgHover="#dbeafe"
          title="Configure Analytics"
          description="Add and manage analytics journey configurations. Define workflows and expand your financial insights."
          cta="Configure"
          ctaColor="#2563eb"
        />
        <Card
          onClick={onExplore}
          icon={<BookOpen style={{ width: 24, height: 24, color: '#059669' }} />}
          iconBg="#ecfdf5" iconBgHover="#d1fae5"
          title="Explore Analytics"
          description="Browse analytics attributes, their definitions, data types, and how they power your financial insights."
          cta="Explore Attributes"
          ctaColor="#059669"
        />
        <Card
          onClick={onTest}
          icon={<FlaskConical style={{ width: 24, height: 24, color: '#7c3aed' }} />}
          iconBg="#f5f3ff" iconBgHover="#ede9fe"
          title="Test Analytics"
          description="Run analytics journeys with real consent data or bank statement PDFs and see live results."
          cta="Start Testing"
          ctaColor="#7c3aed"
        />
      </div>
    </div>
  );
};
